import React from 'react';
import { Task } from '../types/todo';
import { Plane, Building, ListChecks, PartyPopper, Clock, CheckCircle2, ChevronDown, Luggage } from 'lucide-react';
import { TaskItem } from './TaskItem';

interface TaskCategoryProps {
  category: string;
  tasks: Task[];
  allTasks?: Task[];
  isPreview: boolean;
  departureDate?: string;
  returnDate?: string;
  onUpdateStatus: (taskId: string, status: string) => void;
  onDeleteTask: (taskId: string) => void;
  onAddSubtask: (taskId: string) => void;
  onToggleSubtask: (taskId: string, subtaskId: string) => void;
  onUpdateNotes: (taskId: string, notes: string) => void;
  onEditTask: (task: Task) => void;
  onDeleteSubtask: (taskId: string, subtaskId: string) => void;
  onUpdatePriority: (taskId: string, priority: string) => void;
}

export function TaskCategory({
  category,
  tasks,
  allTasks,
  isPreview,
  departureDate,
  returnDate,
  onUpdateStatus,
  onDeleteTask,
  onAddSubtask,
  onToggleSubtask,
  onUpdateNotes,
  onEditTask,
  onDeleteSubtask,
  onUpdatePriority
}: TaskCategoryProps) {
  const [isCollapsed, setIsCollapsed] = React.useState(true);

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'before-departure':
        return <ListChecks className="w-5 h-5 text-blue-500" />;
      case 'after-arrival':
        return <Plane className="w-5 h-5 text-emerald-500" />;
      case 'return':
        return <Building className="w-5 h-5 text-teal-500" />;
      case 'packing':
        return <Luggage className="w-5 h-5 text-amber-500" />;
      default:
        return null;
    }
  };

  const getCategoryLabel = (cat: string) => {
    switch (cat) {
      case 'before-departure':
        return 'Préparer votre départ';
      case 'after-arrival':
        return 'Le jour du départ';
      case 'return':
        return 'Les démarches à l\'arrivée';
      case 'packing':
        return 'Préparer sa valise';
      default:
        return cat;
    }
  };

  const getCategoryStats = () => {
    const tasksToCount = allTasks || tasks;
    const categoryTasks = tasksToCount.filter(t => t.category === category);
    const total = categoryTasks.length;
    const completed = categoryTasks.filter(t => t.status === 'completed').length;
    const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      total,
      completed,
      completionPercentage,
      allCompleted: completed === total && total > 0
    };
  };

  const getDaysUntilDeparture = () => {
    if (!departureDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const departure = new Date(departureDate);
    const diffTime = departure.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const stats = getCategoryStats();
  const daysUntilDeparture = getDaysUntilDeparture();

  const getAccentColor = (cat: string) => {
    switch (cat) {
      case 'before-departure':
        return { bg: 'bg-blue-500', light: 'bg-blue-50 dark:bg-blue-900/20' };
      case 'after-arrival':
        return { bg: 'bg-emerald-500', light: 'bg-emerald-50 dark:bg-emerald-900/20' };
      case 'return':
        return { bg: 'bg-teal-500', light: 'bg-teal-50 dark:bg-teal-900/20' };
      case 'packing':
        return { bg: 'bg-amber-500', light: 'bg-amber-50 dark:bg-amber-900/20' };
      default:
        return { bg: 'bg-gray-500', light: 'bg-gray-50 dark:bg-gray-800' };
    }
  };

  const accent = getAccentColor(category);

  const renderTimeMessage = () => {
    if (category !== 'before-departure' || !departureDate || isPreview) return null;

    if (stats.allCompleted) {
      return (
        <div className="mx-1 sm:mx-3 mb-1.5 sm:mb-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 border border-emerald-100/50 dark:border-emerald-800/30">
          <div className="flex items-center gap-3">
            <PartyPopper className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                Toutes les tâches sont terminées !
              </p>
              {daysUntilDeparture !== null && daysUntilDeparture > 0 && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                  Plus que {daysUntilDeparture} jours avant le départ
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (daysUntilDeparture !== null && daysUntilDeparture > 0) {
      const isUrgent = daysUntilDeparture <= 7;
      const isWarning = daysUntilDeparture <= 30;
      return (
        <div className={`mx-1 sm:mx-3 mb-1.5 sm:mb-2 rounded-xl p-3 border ${
          isUrgent
            ? 'bg-red-50 dark:bg-red-900/20 border-red-100/50 dark:border-red-800/30'
            : isWarning
              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100/50 dark:border-amber-800/30'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-100/50 dark:border-blue-800/30'
        }`}>
          <div className="flex items-center gap-3">
            <Clock className={`w-5 h-5 flex-shrink-0 ${
              isUrgent ? 'text-red-500' : isWarning ? 'text-amber-500' : 'text-blue-500'
            }`} />
            <div>
              <p className={`text-sm font-medium ${
                isUrgent ? 'text-red-800 dark:text-red-300' : isWarning ? 'text-amber-800 dark:text-amber-300' : 'text-blue-800 dark:text-blue-300'
              }`}>
                J-{daysUntilDeparture} avant le départ
              </p>
              <p className={`text-xs mt-0.5 ${
                isUrgent ? 'text-red-600 dark:text-red-400' : isWarning ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
              }`}>
                {stats.total - stats.completed} tâche{stats.total - stats.completed > 1 ? 's' : ''} restante{stats.total - stats.completed > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div id={`category-${category}`} className="scroll-mt-[12rem]">
      <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-2xl border-y sm:border border-gray-100 dark:border-gray-700/50 sm:shadow-sm overflow-hidden">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-full flex items-center gap-3 sm:gap-3 px-4 py-3.5 sm:p-4 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 active:bg-gray-100/50 dark:active:bg-gray-700/50 transition-colors touch-manipulation"
        >
          <div className={`w-10 h-10 sm:w-10 sm:h-10 rounded-xl sm:rounded-xl ${accent.light} flex items-center justify-center flex-shrink-0`}>
            {getCategoryIcon(category)}
          </div>

          <div className="flex-1 min-w-0 text-left">
            <h2 className="text-[15px] sm:text-base font-semibold text-gray-900 dark:text-white truncate">
              {getCategoryLabel(category)}
            </h2>
            <div className="flex items-center gap-2.5 mt-1.5">
              <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden max-w-[140px] sm:max-w-[120px]">
                <div
                  className={`h-full ${accent.bg} rounded-full transition-all duration-500`}
                  style={{ width: `${stats.completionPercentage}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {stats.completed}/{stats.total}
              </span>
            </div>
          </div>

          {stats.allCompleted && (
            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          )}

          <ChevronDown className={`w-5 h-5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            isCollapsed ? '' : 'rotate-180'
          }`} />
        </button>

        {!isCollapsed && (
          <>
            {renderTimeMessage()}

            <div className="sm:px-3 pb-0 sm:pb-3 sm:space-y-2">
              {tasks.map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  isPreview={isPreview}
                  departureDate={departureDate}
                  returnDate={returnDate}
                  onUpdateStatus={onUpdateStatus}
                  onEditTask={onEditTask}
                  onDeleteTask={onDeleteTask}
                  onAddSubtask={onAddSubtask}
                  onToggleSubtask={onToggleSubtask}
                  onUpdateNotes={onUpdateNotes}
                  onDeleteSubtask={onDeleteSubtask}
                  onUpdatePriority={onUpdatePriority}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
